import { Alert, Card, CardBody, Col, Row, Spinner } from "reactstrap";
import {
  IoLayersOutline,
  IoFolderOpenOutline,
  IoBarChartOutline,
  IoWalletOutline,
  IoPricetagOutline,
  IoWarningOutline,
  IoCloseCircleOutline,
  IoAddOutline,
  IoSwapVerticalOutline,
  IoListOutline,
  IoReorderFourOutline,
} from "react-icons/io5";
import { AgGridReact } from "ag-grid-react";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { get } from "../../../../utils/helpers/api";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../context/settings";
import "./styles.css";
import useCurrency from "../../../../utils/helpers/currency";

const formatUnits = (amount) => Number(amount ?? 0).toLocaleString();

const formatMoney = (amount, currency) =>
  `${Number(amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

function StatCard({ icon, iconBg, value, label, hint }) {
  return (
    <Card className="inv-stat-card h-100">
      <CardBody className="d-flex align-items-start gap-3">
        <div className="inv-stat-icon" style={{ background: iconBg }}>
          {icon}
        </div>
        <div>
          <div className="inv-stat-value">{value}</div>
          <div className="inv-stat-label">{label}</div>
          {hint && <div className="inv-stat-hint">{hint}</div>}
        </div>
      </CardBody>
    </Card>
  );
}

function EmptyState({ message }) {
  return (
    <div className="inv-empty-state">
      <IoListOutline size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
      <span>{message}</span>
    </div>
  );
}

function StatusCellRenderer({ data }) {
  const { t } = useTranslation();
  if (data.current_stock === 0) {
    return <span className="inv-badge inv-badge-out">{t('inventory.outOfStock')}</span>;
  }
  return <span className="inv-badge inv-badge-low">{t('inventory.lowStock')}</span>;
}

function MovementTypeCellRenderer({ value }) {
  const { t } = useTranslation();
  if (value === "in") {
    return <span className="inv-badge inv-badge-in">{t('inventory.movementIn')}</span>;
  }
  return <span className="inv-badge inv-badge-out-move">{t('inventory.movementOut')}</span>;
}

export default function HomePage() {
  const { t } = useTranslation();
  const { formatDate } = useSettings();
  const currency = useCurrency();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [kpiData, setKpiData] = useState({
    totalProducts: 0,
    totalCategories: 0,
    totalStockUnits: 0,
    totalStockValue: 0,
    totalStockCost: 0,
    unpricedStockRows: 0,
    lowStockCount: 0,
    outOfStockCount: 0,
  });
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [recentMovements, setRecentMovements] = useState([]);
  const [warehouseBreakdown, setWarehouseBreakdown] = useState([]);

  const getHomeData = async () => {
    setLoading(true);
    const data = await get("/inventory/home");
    if (data?.success) {
      setKpiData({
        totalProducts: data.total_products,
        totalCategories: data.total_categories,
        totalStockUnits: data.total_stock_units,
        // Valued at selling price; the cost basis sits beside it because raw
        // materials and packaging are usually only ever priced to buy.
        totalStockValue: data.total_stock_value,
        totalStockCost: data.total_stock_cost,
        unpricedStockRows: data.unpriced_stock_rows,
        lowStockCount: data.low_stock_count,
        outOfStockCount: data.out_of_stock_count,
      });
      setLowStockProducts(data.low_stock_products);
      setRecentMovements(data.recent_movements);
      setWarehouseBreakdown(data.warehouse_breakdown ?? []);
    }
    setLoading(false);
  };


  useEffect(() => {
    getHomeData();
  }, []);

  const lowStockColDefs = useMemo(() => [
    { field: "name", headerName: t("inventory.products"), flex: 2, sortable: true },
    { field: "sku", headerName: t('inventory.sku'), flex: 1, sortable: true },
    // Stock is per warehouse, so an alert that does not say which one leaves
    // nothing to act on.
    { field: "warehouse_name", headerName: t('warehouses.warehouse'), flex: 1, sortable: true },
    { field: "current_stock", headerName: t("inventory.currentStock").replace(":", ""), flex: 1, sortable: true },
    { field: "minimum_stock", headerName: t("inventory.minimumStock").replace(":", ""), flex: 1, sortable: true },
    {
      headerName: t("common.status"),
      flex: 1,
      cellRenderer: StatusCellRenderer,
      sortable: false,
    },
  ], [t]);

  const warehouseColDefs = useMemo(() => [
    { field: "name", headerName: t('warehouses.warehouse'), flex: 2, sortable: true },
    { field: "code", headerName: t('common.code'), flex: 1, sortable: true },
    {
      field: "units",
      headerName: t('inventory.totalStockUnits'),
      flex: 1,
      sortable: true,
      valueFormatter: (p) => formatUnits(p.value),
    },
    {
      field: "value",
      headerName: t('inventory.totalStockValue'),
      flex: 1,
      sortable: true,
      valueFormatter: (p) => formatMoney(p.value, currency),
    },
    {
      field: "cost_value",
      headerName: t('inventory.totalStockCost'),
      flex: 1,
      sortable: true,
      valueFormatter: (p) => formatMoney(p.value, currency),
    },
  ], [t, currency]);

  const movementsColDefs = useMemo(() => [
    {
      field: "product.name",
      headerName: t("inventory.products"),
      flex: 2,
      sortable: true,
      valueGetter: (params) => params.data?.product?.name ?? "-",
    },
    {
      field: "type",
      headerName: t("common.type"),
      flex: 1,
      sortable: true,
      cellRenderer: MovementTypeCellRenderer,
    },
    { field: "quantity", headerName: t("common.quantity"), flex: 1, sortable: true },
    { field: "reference", headerName: t("common.reference"), flex: 1, sortable: true },
    {
      field: "created_at",
      headerName: t("common.date"),
      flex: 1,
      sortable: true,
      valueFormatter: (p) => formatDate(p.value),
    },
  ], [t, formatDate]);

  const defaultColDef = useMemo(() => ({ resizable: true }), []);

  const quickActions = [
    {
      label: t("inventory.newProduct"),
      icon: <IoAddOutline size={18} color="#6C63FF" />,
      path: "/inventory/products/create",
    },
    {
      label: t("inventory.newAdjustment"),
      icon: <IoSwapVerticalOutline size={18} color="#20c997" />,
      path: "/inventory/stock-adjustments/create",
    },
    {
      label: t("inventory.products"),
      icon: <IoReorderFourOutline size={18} color="#845ef7" />,
      path: "/inventory/products",
    },
    {
      label: t("inventory.stockMovement"),
      icon: <IoBarChartOutline size={18} color="#f76707" />,
      path: "/inventory/stock-movements",
    },
  ];

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t("inventory.title")} onReload={getHomeData} />

        {loading ?
          <Spinner color="primary" className="d-block mx-auto my-4" />
        :
          <div className="subheader-page-content scrollable">
            {/* ── KPI Cards Row 1 ── */}
            <Row className="g-3 mb-3">
              <Col md="4">
                <StatCard
                  icon={<IoLayersOutline size={18} color="#6C63FF" />}
                  iconBg="#f0eeff"
                  value={formatUnits(kpiData.totalProducts)}
                  label={t("inventory.products")}
                />
              </Col>
              <Col md="4">
                <StatCard
                  icon={<IoFolderOpenOutline size={18} color="#845ef7" />}
                  iconBg="#f3f0ff"
                  value={formatUnits(kpiData.totalCategories)}
                  label={t("inventory.categories")}
                />
              </Col>
              <Col md="4">
                <StatCard
                  icon={<IoBarChartOutline size={18} color="#20c997" />}
                  iconBg="#e6faf5"
                  value={formatUnits(kpiData.totalStockUnits)}
                  label={t('inventory.totalStockUnits')}
                />
              </Col>
            </Row>

            {/* ── KPI Cards Row 2 ── */}
            <Row className="g-3 mb-3">
              <Col md="3">
                <StatCard
                  icon={<IoWalletOutline size={18} color="#2f9e44" />}
                  iconBg="#ebfbee"
                  value={formatMoney(kpiData.totalStockValue, currency)}
                  label={t('inventory.totalStockValue')}
                  hint={t('inventory.atSellingPrice')}
                />
              </Col>
              <Col md="3">
                <StatCard
                  icon={<IoPricetagOutline size={18} color="#845ef7" />}
                  iconBg="#f3f0ff"
                  value={formatMoney(kpiData.totalStockCost, currency)}
                  label={t('inventory.totalStockCost')}
                  hint={t('inventory.atCostPrice')}
                />
              </Col>
              <Col md="3">
                <StatCard
                  icon={<IoWarningOutline size={18} color="#f76707" />}
                  iconBg="#fff4e6"
                  value={formatUnits(kpiData.lowStockCount)}
                  label={t('inventory.lowStockProducts')}
                />
              </Col>
              <Col md="3">
                <StatCard
                  icon={<IoCloseCircleOutline size={18} color="#fa5252" />}
                  iconBg="#fff0f0"
                  value={formatUnits(kpiData.outOfStockCount)}
                  label={t('inventory.outOfStockProducts')}
                />
              </Col>
            </Row>

            {/* ── Low Stock Alerts + Quick Actions ── */}
            <Row className="g-3 mb-4" style={{ alignItems: "stretch" }}>
              <Col lg="8" className="d-flex flex-column">
                <h6 className="fw-bold mb-2">{t('inventory.lowStockAlerts')}</h6>
                <Card className="flex-grow-1">
                  <CardBody className="p-0">
                    {lowStockProducts.length === 0 && !loading ? (
                      <EmptyState message={t('inventory.noLowStockAlerts')} />
                    ) : (
                      <div className="ag-theme-alpine inv-table-wrapper" style={{ height: 280 }}>
                        <AgGridReact
                          rowData={lowStockProducts}
                          columnDefs={lowStockColDefs}
                          defaultColDef={defaultColDef}
                          animateRows={true}
                          loading={loading}
                          suppressCellFocus={true}
                          suppressPaginationPanel={true}
                        />
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>

              <Col lg="4" className="d-flex flex-column">
                <h6 className="fw-bold mb-2">{t('inventory.quickActions')}</h6>
                <Card className="flex-grow-1">
                  <CardBody className="d-flex flex-column gap-2">
                    {quickActions.map((action) => (
                      <button
                        key={action.path}
                        className="inv-quick-action-btn"
                        onClick={() => navigate(action.path)}
                      >
                        {action.icon}
                        {action.label}
                      </button>
                    ))}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* ── Stock by Warehouse ── */}
            {warehouseBreakdown.length > 0 && (
              <Row className="g-3 mb-4">
                <Col lg="12" className="d-flex flex-column">
                  <h6 className="fw-bold mb-2">{t('warehouses.stockByWarehouse')}</h6>
                  <Card className="flex-grow-1">
                    <CardBody className="p-0">
                      <div
                        className="ag-theme-alpine inv-table-wrapper"
                        style={{ height: Math.min(warehouseBreakdown.length, 5) * 42 + 50 }}
                      >
                        <AgGridReact
                          rowData={warehouseBreakdown}
                          columnDefs={warehouseColDefs}
                          defaultColDef={defaultColDef}
                          animateRows={true}
                          loading={loading}
                          suppressCellFocus={true}
                          suppressPaginationPanel={true}
                        />
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}

            {/* ── Recent Stock Movements ── */}
            <Row className="g-3">
              <Col lg="12" className="d-flex flex-column">
                <h6 className="fw-bold mb-2">{t('inventory.recentStockMovements')}</h6>
                <Card className="flex-grow-1">
                  <CardBody className="p-0">
                    {recentMovements.length === 0 && !loading ? (
                      <EmptyState message={t('inventory.noStockMovements')} />
                    ) : (
                      <div className="ag-theme-alpine inv-table-wrapper" style={{ height: 300 }}>
                        <AgGridReact
                          rowData={recentMovements}
                          columnDefs={movementsColDefs}
                          defaultColDef={defaultColDef}
                          animateRows={true}
                          loading={loading}
                          suppressCellFocus={true}
                          suppressPaginationPanel={true}
                        />
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        }
      </div>
    </SidebarLayout>
  );
}
